import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import {
  getUserNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
} from '../services/notificationService';
import type { Notification } from '../types/notification';

/**
 * Hook personnalisé pour gérer les notifications utilisateur
 * Polling toutes les 30 secondes pour récupérer les nouvelles notifications
 */
export function useNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Récupère les notifications
  const fetchNotifications = useCallback(async () => {
    if (!user?.user_id) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    try {
      const [notifs, count] = await Promise.all([
        getUserNotifications(user.user_id),
        getUnreadCount(user.user_id),
      ]);

      setNotifications(notifs);
      setUnreadCount(count);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Erreur lors de la récupération des notifications:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [user?.user_id]);

  // Marque une notification comme lue
  const handleMarkAsRead = useCallback(async (notificationId: string) => {
    const success = await markAsRead(notificationId);
    if (success) {
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }
  }, []);

  // Marque toutes les notifications comme lues
  const handleMarkAllAsRead = useCallback(async () => {
    if (!user?.user_id) return;

    const success = await markAllAsRead(user.user_id);
    if (success) {
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    }
  }, [user?.user_id]);

  // Polling toutes les 30 secondes
  useEffect(() => {
    fetchNotifications();

    const interval = setInterval(() => {
      fetchNotifications();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchNotifications]);

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead: handleMarkAsRead,
    markAllAsRead: handleMarkAllAsRead,
    refresh: fetchNotifications,
  };
}